import { defineComponent, ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import { useStore } from 'vuex';
import * as Highcharts from 'highcharts';
import moment from 'moment-timezone';
import Image from '@hems/component/src/image/Image2.vue';
import { FabricHelperPro } from '@hems/util';
import { DAY_HOUR_TO_MIN, MAPPER_PERIOD_DATA, MAPPER_POWER_DEFAULT_VALUE } from '@hems/util/src/constant/mapper';
export default defineComponent({
    name: 'MobileTimeLine',
    components: {
        Image,
    },
    props: {
        timelineData: {
            type: Array,
            default: () => [],
        },
        moduleDayTotalPwData: {
            type: Object,
            default: () => MAPPER_POWER_DEFAULT_VALUE,
        },
        selectedDate: {
            type: String,
            default: '',
        },
        byDate: {
            type: String,
            default: MAPPER_PERIOD_DATA.DAY,
        },
        showTooltip: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['changeToTimeline', 'changeToDay'],
    setup(props, { emit }) {
        const store = useStore();
        const chart = ref(null);
        const maxTime = computed(() => getMaxTime(props.selectedDate));
        const timeValue = ref(maxTime.value);
        const mapperPeriodPartial = [MAPPER_PERIOD_DATA.DAY, MAPPER_PERIOD_DATA.TIMELINE];
        const instance = ref(null);
        const isActiveTimelinePlayBtn = ref(false);
        const startIndex = ref(0);
        const title = computed(() => {
            return props.byDate === MAPPER_PERIOD_DATA.TIMELINE ? 'Total Power' : 'Total Production';
        });
        const xAxis = computed(() => props.timelineData?.map((timelineData) => timelineData.colec_dt));
        const isShowSlider = computed(() => mapperPeriodPartial.includes(props.byDate) && !props.showTooltip);
        const moduleDayTotalPwData = computed(() => props.moduleDayTotalPwData);
        const realTime = computed(() => {
            if (props.timelineData) {
                return FabricHelperPro.convertMinToHour(timeValue.value);
            }
            // 장비를 하루종일 켜지 않아서 15분 데이터가 아예 없는 경우 : 현재 시간 리턴
            return FabricHelperPro.convertMinToHour(maxTime.value);
        });
        const totalPower = computed(() => {
            if (props.byDate === MAPPER_PERIOD_DATA.TIMELINE) {
                // 재생 o
                return FabricHelperPro.roundDownEnergyToSecondDecimalPlaces(props.timelineData?.find((timelineData) => timelineData.colec_dt === FabricHelperPro.convertHourToMin(realTime.value.replace(':', '').slice(-6, -2)))?.module_15min_pw_data_total ?? 0);
            }
            // 재생 x
            return moduleDayTotalPwData.value.convertedEnergy;
        });
        const unit = computed(() => {
            if (props.byDate === MAPPER_PERIOD_DATA.TIMELINE) {
                // 재생 o
                return 'W';
            }
            // 재생 x
            return moduleDayTotalPwData.value.unit;
        });
        function getLocalTime() {
            return moment().tz(store.state.device.baseDeviceInfo?.timezone_id ?? 'Asia/Seoul');
        }
        function getMaxTime(date) {
            const now = new Date();
            if (date) {
                const year = Number(date?.substring(0, 4));
                const month = Number(date?.substring(4, 6));
                const day = Number(date?.substring(6, 8));
                if (now.getDate() !== day || now.getMonth() + 1 !== month || now.getFullYear() !== year) {
                    return DAY_HOUR_TO_MIN;
                }
            }
            const nowTime = getLocalTime().hour() * 60 + getLocalTime().minutes();
            return nowTime;
        }
        const isTimelinePlaying = ref(false);
        const isPlayButtonDisabled = computed(() => props.timelineData.length === 0);
        const timelinePlayBtnImg = computed(() => {
            if (isPlayButtonDisabled.value) {
                return `smartmodule/pro/ic_play_disable_${store.state.appCtx.theme}`;
            }
            return isTimelinePlaying.value
                ? `smartmodule/pro/ic_pause_${store.state.appCtx.theme}`
                : `smartmodule/ic_re_play_${store.state.appCtx.theme}`;
        });
        const onClickTimelineButton = () => {
            if (isPlayButtonDisabled.value)
                return;
            isTimelinePlaying.value ? pauseTimeline(timerIndex) : playTimeline();
        };
        const moveTimelineSliderBar = (event) => {
            if (props.timelineData?.length > 0) {
                // TODO: 10월 mapper pro timeline 작업 시 타입지정 제거
                const sliderValue = event.target.value;
                const validTimelineBarValues = xAxis.value?.filter((colec_dt) => colec_dt <= Number(sliderValue));
                timeValue.value = validTimelineBarValues?.length
                    ? validTimelineBarValues[validTimelineBarValues.length - 1]
                    : xAxis.value[0];
                emit('changeToTimeline', timeValue.value);
            }
        };
        const changeDayMonitoringMode = () => {
            pauseTimeline();
            timeValue.value = maxTime.value;
            startIndex.value = 0;
            emit('changeToDay');
        };
        const changeTimelineToDay = () => {
            if (timer) {
                clearInterval(timer);
            }
            setTimeout(() => {
                changeDayMonitoringMode();
            }, 300);
        };
        const changePlayStatusByDayMode = (timeValue) => {
            if (isShowSlider.value) {
                return emit('changeToTimeline', timeValue);
            }
        };
        const pauseByOpenTooltip = () => {
            if (props.showTooltip) {
                pauseTimeline(timerIndex);
            }
        };
        const resetTimelineOnTimelineEnd = (timerIndex) => {
            if (timerIndex === props.timelineData?.length) {
                changeTimelineToDay();
            }
        };
        const pauseTimeline = (pauseIndex) => {
            isTimelinePlaying.value = false;
            if (timer) {
                clearInterval(timer);
            }
            if (pauseIndex) {
                startIndex.value = pauseIndex;
                timeValue.value = xAxis.value[pauseIndex];
            }
            else {
                startIndex.value = 0;
                timeValue.value = maxTime.value;
            }
            emit('changeToTimeline', timeValue.value);
        };
        // eslint-disable-next-line
        let timer;
        let timerIndex;
        const playTimeline = () => {
            if (props?.timelineData?.length > 0) {
                timeValue.value = xAxis.value[startIndex.value];
                timerIndex = startIndex.value;
                timer = setInterval(() => {
                    if (timeValue.value < maxTime.value) {
                        isTimelinePlaying.value = true;
                        timeValue.value = xAxis.value[timerIndex];
                        timerIndex += 1;
                        changePlayStatusByDayMode(timeValue.value);
                        pauseByOpenTooltip();
                        resetTimelineOnTimelineEnd(timerIndex);
                    }
                    else {
                        changeTimelineToDay();
                    }
                }, 150);
            }
        };
        const getOptions = () => {
            const _data = new Array(DAY_HOUR_TO_MIN).fill(null);
            props.timelineData?.forEach((timelineData) => {
                _data[timelineData.colec_dt] = timelineData.module_15min_pw_data_total;
            });
            const stringXAxis = xAxis.value?.map((xValue) => String(xValue));
            const sliderChartOptions = {
                xAxis: {
                    categories: stringXAxis,
                    lineColor: 'transparent',
                    labels: {
                        enabled: false,
                    },
                },
                yAxis: {
                    title: {
                        text: '',
                    },
                    gridLineColor: 'transparent',
                    labels: {
                        enabled: false,
                    },
                },
                plotOptions: {
                    areaspline: {
                        fillColor: '#00c6c1',
                    },
                    series: {
                        opacity: 0.2,
                        marker: {
                            enabled: false,
                        },
                        lineWidth: 2,
                        states: {
                            hover: {
                                enabled: false,
                            },
                        },
                        enableMouseTracking: false,
                        connectNulls: true,
                    },
                },
                series: [
                    {
                        color: '#008b7e',
                        showInLegend: false,
                        lineColor: 'transparent',
                        data: _data,
                    },
                ],
                tooltip: {
                    enabled: false,
                },
            };
            return sliderChartOptions;
        };
        const update = () => {
            if (!instance.value)
                return;
            const options = getOptions();
            instance.value.update(options, true, true, false);
        };
        const createChart = () => {
            if (!chart.value)
                return;
            const options = getOptions();
            const chartConfig = {
                credits: { enabled: false },
                chart: {
                    type: 'areaspline',
                    height: 30,
                    backgroundColor: 'transparent',
                    renderTo: chart.value,
                    spacingTop: 0,
                    spacingRight: 0,
                    spacingBottom: 0,
                    spacingLeft: 0,
                    plotBorderWidth: 0,
                    margin: [0, 20, 0, 20],
                },
                title: { text: '' },
                subtitle: { text: '' },
                ...options,
            };
            instance.value = Highcharts.chart(chartConfig);
            update(); // watch로 이동 필요
        };
        const destroyChart = () => {
            if (instance.value) {
                instance.value.destroy();
                instance.value = null;
            }
        };
        onMounted(() => {
            changeDayMonitoringMode();
            createChart();
        });
        onBeforeUnmount(() => {
            changeDayMonitoringMode();
            destroyChart();
        });
        watch(() => props.selectedDate, () => {
            if (props.byDate === MAPPER_PERIOD_DATA.TIMELINE) {
                changeDayMonitoringMode();
            }
        });
        watch(() => props.timelineData, () => {
            changeDayMonitoringMode();
            update();
        });
        watch(() => props.byDate, () => {
            if (props.byDate === MAPPER_PERIOD_DATA.DAY) {
                timeValue.value = maxTime.value;
            }
        });
        return {
            store,
            chart,
            realTime,
            totalPower,
            unit,
            title,
            playTimeline,
            moveTimelineSliderBar,
            changeDayMonitoringMode,
            changeTimelineToDay,
            isActiveTimelinePlayBtn,
            startIndex,
            isShowSlider,
            timeValue,
            timelinePlayBtnImg,
            onClickTimelineButton,
            isPlayButtonDisabled,
        };
    },
});
